import { CheckCircle2, Plus } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Can } from '@nucleus-commerce/admin-kit/permissions';
import type { ChangeRequest, OrderWorkflow } from '@nucleus-commerce/api-client/admin';
import { useFormat } from '@nucleus-commerce/i18n/react';
import { Badge } from '@nucleus-commerce/ui/components/badge';
import { Button } from '@nucleus-commerce/ui/components/button';

import { ChangeRequestDialog, ResolveChangeDialog } from './change-request-dialogs';

export interface ChangeRequestsSectionProps {
  storeId: string;
  orderId: string;
  currency: string;
  workflow: OrderWorkflow;
}

/** Changes the customer asked for, from their links or recorded by staff, open ones first. */
export function ChangeRequestsSection({
  storeId,
  orderId,
  currency,
  workflow,
}: ChangeRequestsSectionProps) {
  const { t } = useTranslation('production');
  const format = useFormat();
  const [adding, setAdding] = useState(false);
  const [resolving, setResolving] = useState<ChangeRequest | null>(null);
  const requests = [...workflow.changeRequests].sort(
    (a, b) =>
      Number(a.resolvedAt !== null) - Number(b.resolvedAt !== null) ||
      b.createdAt.localeCompare(a.createdAt),
  );
  const stageName = (key: string | null) =>
    key
      ? (workflow.stages.find((s) => s.key === key)?.name ??
        workflow.stages.flatMap((s) => s.subSteps).find((s) => s.key === key)?.name ??
        key)
      : null;

  return (
    <section className="flex flex-col gap-3 border-t pt-4" aria-labelledby="change-requests-title">
      <div className="flex items-center justify-between gap-2">
        <h3 id="change-requests-title" className="text-sm font-semibold">
          {t('changes.title')}
        </h3>
        <Can permission="orders.write">
          <Button type="button" variant="outline" size="sm" onClick={() => setAdding(true)}>
            <Plus aria-hidden />
            {t('changes.add')}
          </Button>
        </Can>
      </div>
      {requests.length === 0 ? (
        <p className="text-muted-foreground text-sm">{t('changes.empty')}</p>
      ) : (
        <ul className="flex flex-col gap-3" aria-label={t('changes.listLabel')}>
          {requests.map((request) => {
            const stage = stageName(request.stageKey);
            return (
              <li key={request.id} className="flex flex-col gap-1 rounded-md border p-3 text-sm">
                <div className="flex flex-wrap items-center gap-2">
                  <Badge variant="outline">{t(`changes.source.${request.source}`)}</Badge>
                  {request.resolvedAt ? (
                    <Badge
                      variant="outline"
                      className="bg-success/15 text-success border-transparent"
                    >
                      <CheckCircle2 aria-hidden />
                      {t('changes.resolved', {
                        when: format.relative(request.resolvedAt),
                        name: request.resolvedBy?.name ?? t('stage.someone'),
                      })}
                    </Badge>
                  ) : (
                    <Badge
                      variant="outline"
                      className="bg-primary/10 text-foreground border-transparent"
                    >
                      {t('changes.open')}
                    </Badge>
                  )}
                  <span
                    className="text-muted-foreground text-xs"
                    title={format.utc(request.createdAt)}
                  >
                    {format.relative(request.createdAt)}
                  </span>
                </div>
                <p className="whitespace-pre-line">{request.message}</p>
                <p className="text-muted-foreground text-xs">
                  {[
                    stage ? t('changes.stage', { name: stage }) : null,
                    request.extraCost
                      ? t('changes.extraCost', { amount: format.money(request.extraCost) })
                      : null,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </p>
                {request.resolutionNote ? (
                  <p className="text-muted-foreground text-xs">
                    {t('changes.resolution', { note: request.resolutionNote })}
                  </p>
                ) : null}
                {request.resolvedAt ? null : (
                  <Can permission="orders.write">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="self-start"
                      aria-label={t('changes.resolveLabel', { message: request.message })}
                      onClick={() => setResolving(request)}
                    >
                      {t('changes.resolve')}
                    </Button>
                  </Can>
                )}
              </li>
            );
          })}
        </ul>
      )}
      {adding ? (
        <ChangeRequestDialog
          storeId={storeId}
          orderId={orderId}
          currency={currency}
          workflow={workflow}
          onOpenChange={setAdding}
        />
      ) : null}
      {resolving ? (
        <ResolveChangeDialog
          storeId={storeId}
          orderId={orderId}
          currency={currency}
          request={resolving}
          onOpenChange={(open) => {
            if (!open) setResolving(null);
          }}
        />
      ) : null}
    </section>
  );
}
